import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFloorById,
  createFloor,
  updateFloor,
  deleteFloor,
  reorderFloors,
  type FloorFilters,
  type Floor,
  getFloors,
} from '../../services';

// Query keys factory
export const floorKeys = {
  all: ['floors'] as const,
  lists: () => [...floorKeys.all, 'list'] as const,
  list: (filters: FloorFilters) =>
    [...floorKeys.lists(), { ...filters }] as const,
  details: () => [...floorKeys.all, 'detail'] as const,
  detail: (id: number) => [...floorKeys.details(), id] as const,
};

// List query with filters and pagination
export function useFloors(filters: FloorFilters) {
  return useQuery({
    queryKey: floorKeys.list(filters),
    queryFn: () => getFloors(filters),
    placeholderData: (previousData) => previousData,
  });
}

// Single floor query
export function useFloor(id: number) {
  return useQuery({
    queryKey: floorKeys.detail(id),
    queryFn: () => getFloorById(id),
    enabled: !!id,
  });
}

// Create mutation
export function useCreateFloor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFloor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: floorKeys.lists() });
    },
  });
}

// Update mutation
export function useUpdateFloor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Floor> }) =>
      updateFloor(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: floorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: floorKeys.detail(id) });
    },
  });
}

// Delete mutation
export function useDeleteFloor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFloor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: floorKeys.lists() });
    },
  });
}

// Reorder mutation
export function useReorderFloors() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderFloors,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: floorKeys.lists() });
    },
  });
}
